import React, { useEffect, useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getFinancialDateRange, groupAreasWithClinics, groupByClinics, groupDivisionProgramSummery, groupReferralData, makeDownloadPdf } from "./utils";

// ---- Status Box ----
const addStatusBox = (doc, x, y, title, count, color = "#d8cdfb", boxWidth = 40) => {
  const boxHeight = 15;

  // Box
  doc.setFillColor(color);
  doc.roundedRect(x, y, boxWidth, boxHeight, 3, 3, "F");

  // Title
  doc.setFontSize(11);
  doc.setTextColor("#353535");
  doc.text(title, x + boxWidth / 2, y + 6, { align: "center" });

  // Count
  doc.setFontSize(10);
  doc.text(String(count), x + boxWidth / 2, y + 12, { align: "center" });
};

// ---- Heading ----
const addHeading = (
  doc,
  { title = null, subtitle = "", subtitleColor = "#888888", leftText = "", statuses = [], leftTextColor = 0, text = null, boxWidth = 32 },
  startY = 20,
) => {
  console.log('boxWidth', boxWidth);
  let currentY = startY;

  if (title) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const marginX = 20;
    const padding = 5;

    doc.setFontSize(18);
    const titleWidth = doc.getTextWidth(title);
    let boxWidth = titleWidth + padding * 2;

    if (subtitle && subtitle.trim() !== "") {
      doc.setFontSize(10);
      const subtitleWidth = doc.getTextWidth(subtitle);
      boxWidth = Math.max(boxWidth, subtitleWidth + padding * 2);
    }

    const boxX = (pageWidth - boxWidth) / 2;
    const boxY = currentY;
    let boxHeight = 12; // for title line
    if (subtitle && subtitle.trim() !== "") boxHeight += 8;

    // Draw white rounded box
    doc.setDrawColor(200); // light border
    doc.setFillColor(255, 255, 255); // white background
    doc.roundedRect(boxX, boxY, boxWidth, boxHeight, 3, 3, "FD"); // Fill + Draw

    // Add Title
    doc.setFontSize(18);
    doc.setTextColor(40);
    doc.text(title, pageWidth / 2, boxY + 8, { align: "center" });

    // Add Subtitle
    if (subtitle && subtitle.trim() !== "") {
      doc.setFontSize(10);
      doc.setTextColor(subtitleColor || 80);
      doc.text(subtitle, pageWidth / 2, boxY + 16, { align: "center" });
    }

    currentY = boxY + boxHeight + 6; // move cursor after box
  }

  // Subtitle (optional)
  if (text && text.trim() !== "") {
    doc.setFontSize(8);
    doc.setTextColor("#525f7f");
    const pageWidth = doc.internal.pageSize.getWidth();
    const maxWidth = pageWidth - 28; // 14mm margin on left & right

    // Split text into lines that fit within maxWidth
    const lines = doc.splitTextToSize(text, maxWidth);

    // Draw each line centered
    lines.forEach((line) => {
      currentY += 5; // spacing between lines
      doc.text(line, pageWidth / 2, currentY, { align: "center" });
    });

  } else {
    currentY += 10;
  }


  // Left-aligned text (optional)
  if (leftText && leftText.trim() !== "") {
    doc.setFontSize(12);
    doc.setTextColor(leftTextColor);
    doc.text(leftText, 14, currentY + 8);
  }

  // Status boxes (optional)
  if (Array.isArray(statuses) && statuses.length > 0) {

    const gap = 5;
    const startYBoxes = currentY;
    const totalWidth = statuses.length * boxWidth + (statuses.length - 1) * gap;
    let startX = doc.internal.pageSize.getWidth() - 14 - totalWidth;

    statuses.forEach((s) => {
      addStatusBox(doc, startX, startYBoxes, s.title, s.count, s.color, boxWidth);
      startX += boxWidth + gap;
    });

    currentY += 20; // spacing after boxes
  } else {
    currentY += 10; // spacing after heading if no statuses
  }

  return currentY; // Y position after heading + subtitle + boxes
};


const addBackgroundColor = (doc, color = "#f5f5f5") => {
  doc.setFillColor(color);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight(), "F");
};

const addHeader = (doc, leftLogo, rightLogo, headerY = 5, logoHeight = 15) => {
  const pageWidth = doc.internal.pageSize.getWidth();

  if (leftLogo) {
    doc.addImage(leftLogo, "PNG", 10, headerY - 8, 30, 0); // left side
  }
  if (rightLogo) {
    doc.addImage(rightLogo, "PNG", pageWidth - 40, headerY, 30, 0); // right side
  }
};

// ---- Table ----
const addTable = (doc, tableData, startY = 50, options = {}) => {
  const headColor = options.headColor || [255, 0, 0];
  const lineWidth = options.lineWidth !== undefined ? options.lineWidth : 0.2;

  autoTable(doc, {
    startY,
    head: tableData.head,
    body: tableData.body,
    margin: { top: 30 },

    styles: {
      halign: "center",
      valign: "middle",
      fillColor: [255, 255, 255],
      lineWidth,
      lineColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: headColor,
      textColor: "#777",
      lineWidth,
      lineColor: [0, 0, 0],
    },

    alternateRowStyles: { fillColor: [255, 255, 255] },
  });

  return doc.lastAutoTable.finalY + 10; // return Y after table
};


// Function to load image as base64
const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // allow loading from public
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = url;
  });
};

// Add background image to a page
const addBackgroundImage = (doc, imgData) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  doc.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);
};


const makeDivisonTableData = (referralData) => {
  let formData = localStorage.getItem("formData");
  if (formData) {
    formData = JSON.parse(formData);
  }
  const divisonTableData = groupDivisionProgramSummery(referralData);
  const areaTableData = groupAreasWithClinics(referralData);
  const bodyData = [];
  divisonTableData.data.map((i) => {
    let length = i.programs.length;
    i.programs.map((p, index) => {
      if (index == 0) {
        bodyData.push([
          { content: i.areaName, rowSpan: length, styles: { valign: "middle" } },
          p.count,
          p.name,
        ]);
      } else {
        bodyData.push([
          p.count,
          p.name,
        ]);
      }
    })

  })

  const body2 = [];
  console.log(areaTableData)
  areaTableData.map((item, index) => {
    const clinicData = [];
    item.clinics.map((val) => {
      let length = val.programs.length;
      val.programs.map((p, index) => {
        if (index == 0) {
          clinicData.push([
            { content: val.clinicName, rowSpan: length, styles: { valign: "middle" } },
            p.programName,
            p.count,
            p.onboard,
            p.sms,
            p.whatsapp,
            p.other
          ]);
        } else {
          clinicData.push([
            p.programName,
            p.count,
            p.onboard,
            p.sms,
            p.whatsapp,
            p.other
          ]);
        }
        return val;
      })
      return val;
    })

    body2.push({
      heading: {
        title: index == 0 ? "Practice-level & Source Split" : null,
        leftText: item.areaName,
        leftTextColor: '#53b73e',
        statuses: [{ title: "Total Patients", count: item.totalClinics, color: "#d6f0fb" }],
      },
      tableData: {
        head: [["Practice", "Programe", "Total", "Total Onboard", "SMS", "WhatsApp", "Other"]],
        body: clinicData,
      },
      headColor: "#e4ecf8",
    });
    return item;
  })


  return [
    {
      heading: {
        title: "Onboarded Report",
        subtitle: `${formData._fromDate} - ${formData._toDate}`, // optional
        subtitleColor: "#ff9d19",
        leftTextColor: '#53b73e',
        leftText: "Division & program summary",   // optional
        statuses: [
          { title: "Total Onboarded", count: divisonTableData.onboardedUser, color: "#e0faff" },
          { title: "Accounts Created", count: divisonTableData.total, color: "#d8cdfb" },
        ], // optional
      },
      // Division	Account Created	Programe
      tableData: {
        head: [["Division", "Account Created", "Programe"]],
        body: bodyData,
      },
      headColor: "#e4ecf8",
    },
    ...body2
  ];

}
const percentageActive = (total, active) => {
  if (total === 0) return (0).toFixed(2); // Avoid division by zero
  return ((active / total) * 100).toFixed(2);
};
const makeReffralTableData = (referralData) => {
  let formData = localStorage.getItem("formData");
  if (formData) {
    formData = JSON.parse(formData);
  }
  const date = getFinancialDateRange();
  const bodyData = [];
  const clinicsData = groupByClinics(referralData);

  // <tr key={`divison_table_${index}`}>
  //                         <th className="break-line">
  //                             {item.clinicName}
  //                         </th>
  //                         <td>{item.onboard||0}</td>
  //                         <td>{item.count}</td>
  //                         <td>{item?.sms || 0}</td>
  //                         <td>{item?.whatsapp || 0}</td>
  //                         <td>{item?.other || 0}</td>
  //                     </tr>
  clinicsData.map((item) => {
    bodyData.push([
      item.clinicName,
      item.onboard || 0,
      item.count || 0,
      item.sms || 0,
      item.whatsapp || 0,
      item.other || 0
    ])
    return item;
  })

  const {
    byProgram,
    byClinic,
    byArea
  } = groupReferralData(referralData);
  const programTableData = [];
  const clinicTableData = [];

  byProgram.labels.map((val, index) => {
    programTableData.push([
      val,
      byProgram.datasets[0].data[index],
      byProgram.datasets[1].data[index],
      byProgram.datasets[2].data[index],
    ])
  })

  byClinic.labels.map((val, index) => {
    clinicTableData.push([
      val,
      byClinic.datasets[0].data[index],
      byClinic.datasets[1].data[index],
      byClinic.datasets[2].data[index],
    ])
  })


  return [
    {
      heading: {
        title: "Division Report",
        subtitle: `Practice-Wise Account Performance: ${formData?.division?.label ?? "All"}`, // optional
        text: `Breakdown of accounts created and referred for each practice, both year-to-date (${date}) and for the selected ( ${formData._fromDate} to  ${formData._toDate} ), along with a yearly source-wise split (SMS,WhatsApp, Others)`,
        subtitleColor: "#525f7f",
        leftTextColor: '#53b73e',
        // leftText: "Division & program summary",   // optional
        // statuses: [
        //   { title: "Total Onboarded", count: divisonTableData.onboardedUser, color: "#e0faff" },
        //   { title: "Grant Total", count: divisonTableData.total, color: "#d8cdfb" },
        // ], // optional
      },
      // Division	Account Created	Programe
      tableData: {
        head: [
          [
            { content: "Period", styles: { halign: "center", valign: "middle" } },
            { content: `${formData._fromDate} to ${formData._toDate}`, styles: { halign: "center" } },
            { content: date, colSpan: 4, styles: { halign: "center", valign: "middle" } }
          ],
          [
            { content: "Practice Name", styles: { halign: "center" } },
            { content: "Total Onboarded", styles: { halign: "center" } },
            { content: "Account Created", styles: { halign: "center" } },
            { content: "Referred Source (SMS)", styles: { halign: "center" } },
            { content: "Referred Source (WhatsApp)", styles: { halign: "center" } },
            { content: "Referred Source (Others)", styles: { halign: "center" } },
          ],
        ],
        body: bodyData,
        headStyles: {
          fillColor: [186, 242, 210], // light green like screenshot
          textColor: [0, 0, 0],
          halign: "center",
          valign: "middle",
        },
        styles: {
          halign: "center",
          valign: "middle",
        },
      },
      headColor: "#baf2d2",
    },
    {
      isThreeTable: true
    },
    {
      heading: {

        leftTextColor: '#53b73e',
        leftText: "Division & program summary",   // optional
        statuses: [
          { title: "Total", count: byProgram?.totalData?.total || 0, color: "#d6f0fb" },
          { title: "Active", count: byProgram?.totalData?.active || 0, color: "#d8cdfb" },
          { title: "Inactive", count: byProgram?.totalData?.inactive || 0, color: "#e0fbd6" },
          { title: "% Active", count: percentageActive(byProgram?.totalData?.total, byProgram?.totalData?.active), color: "#fbd7d6" },
        ], // optional
        boxWidth: 20,
      },
      // Division	Account Created	Programe
      tableData: {
        head: [
          [
            { content: "Program", styles: { halign: "center", valign: "middle" } },
            { content: "App Downloaded", styles: { halign: "center", valign: "middle" } },
            { content: "Active", styles: { halign: "center", valign: "middle" } },
            { content: "Inactive", styles: { halign: "center", valign: "middle" } },
          ],
        ],
        body: programTableData,
        headStyles: {
          fillColor: [186, 242, 210], // light green like screenshot
          textColor: [0, 0, 0],
          halign: "center",
          valign: "middle",
        },
        styles: {
          halign: "center",
          valign: "middle",
        },
      },
      headColor: "#baf2d2",
    },
    {
      heading: {

        leftTextColor: '#53b73e',
        leftText: "Clinic summary",   // optional
        statuses: [
          { title: "Total", count: byProgram?.totalData?.total || 0, color: "#d6f0fb" },
          { title: "Active", count: byProgram?.totalData?.active || 0, color: "#d8cdfb" },
          { title: "Inactive", count: byProgram?.totalData?.inactive || 0, color: "#e0fbd6" },
          { title: "% Active", count: percentageActive(byProgram?.totalData?.total, byProgram?.totalData?.active), color: "#fbd7d6" },
        ], // optional
        boxWidth: 20,
      },
      // Division	Account Created	Programe
      tableData: {
        head: [
          [
            { content: "Clinic", styles: { halign: "center", valign: "middle" } },
            { content: "App Downloaded", styles: { halign: "center", valign: "middle" } },
            { content: "Active", styles: { halign: "center", valign: "middle" } },
            { content: "Inactive", styles: { halign: "center", valign: "middle" } },
          ],
        ],
        body: clinicTableData,
        headStyles: {
          fillColor: [186, 242, 210], // light green like screenshot
          textColor: [0, 0, 0],
          halign: "center",
          valign: "middle",
        },
        styles: {
          halign: "center",
          valign: "middle",
        },
      },
      headColor: "#baf2d2",
    },
    // ...body2
  ];
}

const makeThreeTable = (doc, startY = 20, referralData) => {

  let currentY = startY;
  currentY += 10;
  // Title
  doc.setFontSize(14);
  doc.setTextColor(40);
  doc.text("Overall Coaching Insights", doc.internal.pageSize.getWidth() / 2, currentY, { align: "center" });
  currentY += 10;

  doc.setFontSize(12);
  doc.setTextColor(0);
  doc.text("Call Insights", 25, currentY);

  doc.setFontSize(12);
  doc.setTextColor(0);
  doc.text("Messaging Insights", 80, currentY);

  doc.setFontSize(12);
  doc.setTextColor(0);
  doc.text("Rating Insights", 150, currentY);

  const pageWidth = doc.internal.pageSize.getWidth();
  const gap = 5; // gap between tables
  const tableWidth = (pageWidth - 4 * gap) / 3; // divide page into 3 equal parts

  // Column widths
  const callCols = [tableWidth * 0.4, tableWidth * 0.6];
  const messageCols = [tableWidth * 0.3, tableWidth * 0.35, tableWidth * 0.35];
  const ratingCols = [tableWidth * 0.3, tableWidth * 0.4, tableWidth * 0.3];

  currentY += 5;
  autoTable(doc, {
    startY: currentY,
    head: [[
      // Call Table Header
      { content: "Period", styles: { halign: "center", fillColor: "#baf2d2" }, cellWidth: callCols[0] },
      { content: "Total Calls\nMade by Coaches", styles: { halign: "center", fillColor: "#baf2d2" }, cellWidth: callCols[1] },

      // Gap column (no border)
      { content: "", styles: { fillColor: [255, 255, 255], lineWidth: 0 }, cellWidth: gap },

      // Message Table Header
      { content: "Period", styles: { halign: "center", fillColor: "#baf2d2" }, cellWidth: messageCols[0] },
      { content: "Messages\nSent by Coach", styles: { halign: "center", fillColor: "#baf2d2" }, cellWidth: messageCols[1] },
      { content: "Messages\nSent by Patient", styles: { halign: "center", fillColor: "#baf2d2" }, cellWidth: messageCols[2] },

      // Gap column (no border)
      { content: "", styles: { fillColor: [255, 255, 255], lineWidth: 0 }, cellWidth: gap },

      // Rating Table Header
      { content: "Period", styles: { halign: "center", fillColor: "#baf2d2" }, cellWidth: ratingCols[0] },
      { content: "Total Ratings\nReceive", styles: { halign: "center", fillColor: "#baf2d2" }, cellWidth: ratingCols[1] },
      { content: "Average\nRating", styles: { halign: "center", fillColor: "#baf2d2" }, cellWidth: ratingCols[2] },
    ]],
    body: [
      [
        "Weekly", "149", "", "Weekly", "12057", "12312", "", "Weekly", "109", "4.7"
      ],
      [
        "Yearly", "465", "", "Yearly", "49408", "51946", "", "Yearly", "620", "4.6"
      ]
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 3 },
    headStyles: { fillColor: [200, 255, 200], halign: "center", textColor: 0 },
    columnStyles: {
      2: { lineWidth: 0 }, // remove line for first gap
      6: { lineWidth: 0 }  // remove line for second gap
    }
  });
  return currentY;
};

// ---- React Component ----
const ReportPDF = () => {
  const [referralData, setReferralData] = useState([]);

  useEffect(() => {
    let _referralData = localStorage.getItem("referralData");

    if (_referralData) {
      _referralData = JSON.parse(_referralData);
      setReferralData(_referralData);
    }
  }, []);

  const downloadPDF = async () => {
    makeDownloadPdf({referralData})
    // const doc = new jsPDF("p", "mm", "a4");
    // const pageHeight = doc.internal.pageSize.getHeight();
    // const pageWidth = doc.internal.pageSize.getWidth();
    // let currentY = 20;

    // const bgImage = await loadImageAsBase64("/report/bg.png");
    // const leftLogo = await loadImageAsBase64("/report/goqii.png");
    // const rightLogo = await loadImageAsBase64("/report/modality.png");

    // // Draw background + header for first page
    // addBackgroundColor(doc, "#f7fafe");
    // // doc.addImage(bgImage, "PNG", 0, 0, pageWidth, pageHeight); // optional
    // addHeader(doc, leftLogo, rightLogo);

    // makeDivisonTableData(referralData).forEach((item, index) => {
    //   const estimatedTableHeight =
    //     ((item?.tableData?.body?.length ?? (item?.isThreeTable ? 5 : 0)) + 1) * 10;

    //   // Add new page if needed
    //   if (index > 0 && currentY + 30 + estimatedTableHeight > pageHeight) {
    //     doc.addPage();
    //     currentY = 20;
    //     // Always redraw background + header on new page
    //     addBackgroundColor(doc, "#f7fafe");
    //     // doc.addImage(bgImage, "PNG", 0, 0, pageWidth, pageHeight); // optional
    //     addHeader(doc, leftLogo, rightLogo);
    //   }

    //   if (!item?.isThreeTable) {
    //     currentY = addHeading(doc, item.heading, currentY);
    //     currentY = addTable(doc, item.tableData, currentY, { headColor: item.headColor, leftLogo, rightLogo });
    //   } else {
    //     currentY = makeThreeTable(doc, currentY, referralData);
    //   }
    // });

    // doc.save("report.pdf");
  };

  return (
    <button onClick={downloadPDF}>
      Download PDF
    </button>
  );
};


export default ReportPDF;
